import React, { useState } from 'react';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    interest: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const validateField = (name, value) => {
    switch (name) {
      case 'name':
        return value.trim() ? '' : 'Please enter your name';
      case 'email':
        if (!value.trim()) return 'Please enter your email';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Please enter a valid email';
        return '';
      case 'message':
        return value.trim() ? '' : 'Please enter your message';
      default:
        return '';
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (touched[name]) {
      setErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    setErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
  };

  const handleSubmit = (e) => {
    const newErrors = {};
    ['name', 'email', 'message'].forEach(field => {
      const error = validateField(field, formData[field]);
      if (error) newErrors[field] = error;
    });

    if (Object.keys(newErrors).length > 0) {
      e.preventDefault();
      setErrors(newErrors);
      setTouched({ name: true, email: true, message: true });
      
      const firstErrorField = Object.keys(newErrors)[0];
      document.getElementById(firstErrorField)?.focus();
    }
  };

  return (
    <main id="main-content" className="contact-page" role="main">
      <section className="contact-hero" aria-labelledby="contact-heading">
        <div className="container">
          <span className="contact-label">Contact</span>
          <h1 id="contact-heading" className="contact-title">
            Partnerships, pilots and<br />
            <span className="highlight">product feedback</span>
          </h1>
          <p className="contact-intro">
            Contact BACIMO about live product fit, pilot access, integration partnerships,
            or product feedback. The form routes directly to the team and keeps the
            conversation focused on practical next steps.
          </p>
        </div>
      </section>

      <section className="contact-content" aria-label="Contact information and form">
        <div className="container">
          <div className="contact-grid">
            <div className="contact-info">
              <div className="info-block">
                <h2>Executive contact, without the noise</h2>
                <p>
                  Reach out for pilots, partnerships, implementation discussions,
                  or grounded feedback on BACIMO products already in use.
                </p>
              </div>

              <div className="info-block">
                <h3>Best For</h3>
                <p>
                  Product evaluations, controlled pilot rollouts, workflow reviews,
                  and partnership conversations with teams that need practical AI.
                </p>
              </div>

              <div className="info-block">
                <h3>Direct Email</h3>
                <a href="mailto:info@bacimo.net" className="email-link">
                  info@bacimo.net
                </a>
              </div>

              <div className="info-block">
                <h3>Response Window</h3>
                <p>Usually within 24-48 hours from Bratislava, Slovakia.</p>
              </div>
            </div>

            <form
              className="contact-form"
              action="https://formsubmit.co/info@bacimo.net"
              method="POST"
              onSubmit={handleSubmit}
              noValidate
              aria-label="Contact form"
            >
              <input type="hidden" name="_subject" value="New contact from BACIMO website" />
              <input type="hidden" name="_captcha" value="false" />
              <input type="text" name="_honey" style={{ display: 'none' }} tabIndex={-1} autoComplete="off" aria-hidden="true" />

              <div className={`form-group ${errors.name && touched.name ? 'has-error' : ''}`}>
                <label htmlFor="name">
                  Name <span className="required" aria-hidden="true">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  aria-required="true"
                  aria-invalid={errors.name && touched.name ? 'true' : 'false'}
                  aria-describedby={errors.name ? 'name-error' : undefined}
                  placeholder="Your name"
                />
                {errors.name && touched.name && (
                  <span id="name-error" className="form-error" role="alert">
                    {errors.name}
                  </span>
                )}
              </div>

              <div className={`form-group ${errors.email && touched.email ? 'has-error' : ''}`}>
                <label htmlFor="email">
                  Email <span className="required" aria-hidden="true">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  aria-required="true"
                  aria-invalid={errors.email && touched.email ? 'true' : 'false'}
                  aria-describedby={errors.email ? 'email-error' : undefined}
                  placeholder="your@email.com"
                  autoComplete="email"
                />
                {errors.email && touched.email && (
                  <span id="email-error" className="form-error" role="alert">
                    {errors.email}
                  </span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="company">Company</label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  placeholder="Your company (optional)"
                  autoComplete="organization"
                />
              </div>

              <div className="form-group">
                <label htmlFor="interest">Interested In</label>
                <select
                  id="interest"
                  name="interest"
                  value={formData.interest}
                  onChange={handleChange}
                >
                  <option value="">Select a topic</option>
                  <option value="BaciCheck">BaciCheck</option>
                  <option value="BacimOCR">BacimOCR</option>
                  <option value="Pilot access">Pilot access</option>
                  <option value="Product feedback">Product feedback</option>
                  <option value="Partnership">Partnership</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className={`form-group ${errors.message && touched.message ? 'has-error' : ''}`}>
                <label htmlFor="message">
                  Message <span className="required" aria-hidden="true">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  aria-required="true"
                  aria-invalid={errors.message && touched.message ? 'true' : 'false'}
                  aria-describedby={errors.message ? 'message-error' : undefined}
                  rows="5"
                  placeholder="Share your use case, pilot scope, partnership context, or product feedback..."
                ></textarea>
                {errors.message && touched.message && (
                  <span id="message-error" className="form-error" role="alert">
                    {errors.message}
                  </span>
                )}
              </div>

              <button type="submit" className="btn btn-primary submit-btn">
                Send Message <span aria-hidden="true">→</span>
              </button>
            </form>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Contact;
